import { rest, stdParamsSerializer } from "src/common/rest";
import { apiMeasurementChart, apiObjUrl } from "src/common/apiUrl";
import { zEntity, ZEntity } from "src/types/ZEntity";
import { z } from "zod";
import { zIdName } from "src/types/ZIdName";
import { ZUid } from "src/types/zUid";
import {
  NewMeasurementChartEntityData,
  zMCPoint,
  ZMCPoint,
  zMCPointValue,
  ZMCPointValue,
  ZMCRow,
  zMeasurementChart,
  ZMeasurementChart,
} from "./ZMeasurementChart";
import { ZMesChartSettings, zMesChartSettings } from "./ZMesChartSettings";
import { NumOption } from "./MChartEntity/EditMCEntity/MChartTable/PointDict";
import { zMcSizeVersions, ZMcSizeVersions } from "./ZMcSizeVersions";

export const loadBaseSizes = async (
  servObjId: ZUid,
  sizeId: ZUid,
): Promise<NumOption[]> => {
  const resp = await rest.get(
    apiMeasurementChart(`/copy/${servObjId}/${sizeId}`),
  );
  const list = zIdName.array().parse(resp.data);
  return list.map(({ id, name }) => ({ value: id, label: name }));
};

export const loadEntitiesAsRef = async (objectId: ZUid): Promise<ZEntity[]> => {
  const resp = await rest.post(
    apiObjUrl(`/entities/search`),
    { objectId },
    { params: { page: 0, size: 1000 } },
  );
  const zResp = z.object({
    content: zEntity.array(),
  });
  return zResp.parse(resp.data).content;
};

export const loadMesChartSettings = async (servObjId: ZUid) => {
  const resp = await rest.get(apiMeasurementChart(`/settings/${servObjId}`));
  return zMesChartSettings.parse(resp.data);
};

export const saveMesChartSettings = async (
  servObjId: ZUid,
  data: ZMesChartSettings,
) => {
  await rest.put(apiMeasurementChart(`/settings/${servObjId}`), data);
};

export const loadMeasurementChart = async (
  modelId: ZUid,
): Promise<ZMeasurementChart> => {
  const resp = await rest.get(apiMeasurementChart(`/entity/${modelId}`));
  return zMeasurementChart.parse(resp.data);
};

export const scaleMeasurementChart = async (
  modelId: ZUid,
  pointIds: ZUid[],
): Promise<ZMeasurementChart> => {
  const resp = await rest.put(
    apiMeasurementChart(`/scale/${modelId}`),
    pointIds,
  );
  return zMeasurementChart.parse(resp.data);
};

export const createMeasurementChart = async (
  data: NewMeasurementChartEntityData,
): Promise<ZMeasurementChart> => {
  const resp = await rest.post(apiMeasurementChart("/entity"), data);
  return zMeasurementChart.parse(resp.data);
};

export const deleteMeasurementChart = async (modelId: ZUid): Promise<void> => {
  await rest.delete(apiMeasurementChart(`/entity/${modelId}`));
};

export const copyMeasurementChart = async (
  sourceEntityId: ZUid,
  targetEntityId: ZUid,
) => {
  await rest.put(apiMeasurementChart("/copy"), undefined, {
    params: { sourceEntityId, targetEntityId },
  });
};

export const saveMcPointValue = async (
  modelId: ZUid,
  pointValue: ZMCPointValue,
): Promise<ZMCPointValue> => {
  const resp = await rest.put(
    apiMeasurementChart(`/point-value/${modelId}`),
    pointValue,
  );
  return zMCPointValue.parse(resp.data);
};

export const saveMcPontField = async <Field extends keyof ZMCPoint>(
  modelId: ZUid,
  field: Field,
  point: ZMCPoint,
): Promise<ZMCPoint> => {
  const resp = await rest.put(
    apiMeasurementChart(`/mc-pont/${modelId}`),
    point,
    {
      params: {
        target: field,
      },
    },
  );
  return zMCPoint.parse(resp.data);
};

// POST /api/row/{entityId} Создание новой строки
export type NewMcRowData = {
  pointDto: {
    id: ZUid;
    name: string;
  };
  description: string;
};
export const createNewMcRows = async (
  modelId: ZUid,
  rows: NewMcRowData[],
): Promise<void> => {
  await rest.post(apiMeasurementChart(`/rows/${modelId}`), rows);
};

// DELETE /api/row/{entityId} Удаление строки
export const deleteMcRows = async (
  modelId: ZUid,
  rows: ZMCRow[],
): Promise<void> => {
  const data: ZUid[] = rows.map(({ mcPoint }) => mcPoint.id);
  await rest.delete(apiMeasurementChart(`/row/${modelId}`), { data });
};

export const addMcVersionToSize = async (
  modelId: ZUid,
  sizeId: ZUid,
  pointValue: Omit<ZMCPointValue, "id">,
) => {
  // POST /api/point-value/{entityId}/{sizeId}
  await rest.post(
    apiMeasurementChart(`/point-value/${modelId}/${sizeId}`),
    pointValue,
  );
};

export const deleteMcVersion = async (modelId: ZUid, data: ZMCPointValue) => {
  await rest.delete(apiMeasurementChart(`/point-value/${modelId}`), { data });
};

export const loadMcSizeVersion = async (
  modelId: ZUid,
): Promise<ZMcSizeVersions[]> => {
  const resp = await rest.get(apiMeasurementChart("/size-version"), {
    params: {
      entityId: modelId,
    },
  });
  return zMcSizeVersions.array().parse(resp.data);
};

export const copyMcPoints = async (
  sourceEntityId: ZUid,
  targetEntityIds: ZUid[],
  pointIds: ZUid[],
) => {
  await rest.put(apiMeasurementChart("/copy/values"), pointIds, {
    params: { sourceEntityId, targetEntityIds },
    paramsSerializer: stdParamsSerializer,
  });
};
